import { h } from 'preact';

import { SCENE_PALETTE, FactoryWall } from './progressSceneParts';
import { getSaleDisplayTitle } from '../../lib/saleDisplayTitle';

// Progress scene shown while «Send alle» transfers sales to the accounting
// system (Tripletex, Fiken, PowerOffice, Duett). Driven by the real bulk loops
// in allVisitsTable, which advance `current` once per completed sale. The <svg>
// is keyed on `current`, so every completed sale remounts the scene: the cloud
// pulses for the envelope that just arrived, and the next sheet leaves the
// tray, gets folded into an envelope at the station and flies up towards the
// cloud, where it hovers until the API answers (= the next remount). Tying the
// hand-off to the real step means the scene never looks dead on a slow call.
//
// Styles live in a literal <style> inside the <svg> (same trick as the
// pull-to-refresh scenes) so preact-cli's CSS Modules hashing can't touch the
// class names.

export const MAX_VISIBLE_TRAY = 8;

// «#1000283 Stian Koll - Fido (7 år)» — what the loops show as the sale in flight.
export function transferLabel(sale) {
    if (!sale) {
        return '';
    }
    const number = sale.saleNumber || sale.id || '';
    const title = getSaleDisplayTitle(sale);
    return `#${number} ${title === 'no title' ? '' : title}`.trim();
}

const SCENE_CSS = `
.at-scene .at-anim {
    transform-box: fill-box;
    transform-origin: center;
}
@keyframes atSheetIn {
    from { transform: translate(-250px, 26px); opacity: 0; }
    20%  { opacity: 1; }
    to   { transform: translate(0, 0); opacity: 1; }
}
@keyframes atFold {
    from { transform: scaleY(1);   opacity: 1; }
    80%  { opacity: 1; }
    to   { transform: scaleY(0.2); opacity: 0; }
}
@keyframes atEnvelopeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}
@keyframes atFly {
    0%   { transform: translate(0, 0) rotate(0deg); }
    50%  { transform: translate(90px, -92px) rotate(-10deg); }
    100% { transform: translate(172px, -34px) rotate(0deg); }
}
@keyframes atHover {
    0%, 100% { transform: translate(172px, -34px); }
    50%      { transform: translate(172px, -41px); }
}
@keyframes atDash {
    from { stroke-dashoffset: 0; }
    to   { stroke-dashoffset: -24; }
}
@keyframes atBlink {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.15; }
}
@keyframes atPulse {
    from { opacity: 0.9; transform: scale(0.7); }
    to   { opacity: 0;   transform: scale(1.35); }
}
@media (prefers-reduced-motion: reduce) {
    .at-scene .at-anim { animation: none !important; }
    .at-scene .at-sheet { opacity: 1 !important; }
    .at-scene .at-envelope { opacity: 1 !important; transform: translate(172px, -34px) !important; }
    .at-scene .at-pulse { opacity: 0 !important; }
}
`;

// Cloud drawn twice: an outline pass (edge colour, slightly larger) under the fill pass.
const CLOUD_BLOBS = [[650, 78, 60, 26], [620, 66, 28, 20], [665, 58, 34, 24], [692, 74, 26, 18]];
const CloudShape = ({ p }) => (
    <g>
        {CLOUD_BLOBS.map(([cx, cy, rx, ry]) => (
            <ellipse key={`e${cx}`} cx={cx} cy={cy} rx={rx + 2.5} ry={ry + 2.5} fill={p.cloudEdge} />
        ))}
        {CLOUD_BLOBS.map(([cx, cy, rx, ry]) => (
            <ellipse key={`f${cx}`} cx={cx} cy={cy} rx={rx} ry={ry} fill={p.cloud} />
        ))}
    </g>
);

const Badge = ({ x, y, fill, count, label, countClass, p }) => (
    <g>
        <circle cx={x} cy={y} r='15' fill={fill} />
        <text class={countClass} x={x} y={y + 5} text-anchor='middle' font-family='sans-serif' font-size='14' font-weight='700' fill='#ffffff'>{count}</text>
        <text x={x} y={y + 30} text-anchor='middle' font-family='sans-serif' font-size='10' font-weight='600' fill={p.text}>{label}</text>
    </g>
);

const AccountingTransferProgress = ({
    current = 0, total = 0, systemName = '', label = '', failed = 0,
}) => {
    const p = SCENE_PALETTE;
    const safeCurrent = Math.max(current, 0);
    const safeFailed = Math.max(failed, 0);
    const inFlight = safeCurrent < total;
    const waiting = Math.max(total - safeCurrent - 1, 0);
    const visibleTray = Math.min(waiting, MAX_VISIBLE_TRAY);
    const trayOverflow = Math.max(waiting - MAX_VISIBLE_TRAY, 0);
    const sent = Math.max(safeCurrent - safeFailed, 0);
    const pct = total > 0 ? Math.round((Math.min(safeCurrent, total) / total) * 100) : 0;

    const trayIndexes = [];
    for (let i = 0; i < visibleTray; i += 1) {
        trayIndexes.push(i);
    }

    return (
        <div class='mt-2 rounded-3 overflow-hidden border'>
            <svg
                key={safeCurrent}
                viewBox='0 0 800 230'
                preserveAspectRatio='xMidYMid meet'
                role='img'
                class='at-scene'
                style={`display: block; width: 100%; height: auto; max-height: 260px; overflow: visible; background: ${p.backdrop};`}
            >
                <style>{SCENE_CSS}</style>
                <title>Overfører til {systemName}</title>

                <FactoryWall p={p} patternId='at-bricks' />

                {/* Loading-dock floor */}
                <rect x='0' y='174' width='800' height='26' fill={p.belt} />
                {Array.from({ length: 20 }, (_, i) => (
                    <rect key={i} x={i * 40 + 8} y='194' width='20' height='6' fill={p.beltStripe} opacity='0.7' />
                ))}

                {/* Dashed flight path from the station to the cloud */}
                <path
                    class='at-anim'
                    d='M400 100 Q 470 -10 572 72'
                    fill='none'
                    stroke={p.cloudEdge}
                    stroke-width='2'
                    stroke-dasharray='6 6'
                    opacity='0.55'
                    style='animation: atDash 0.8s linear infinite;'
                />

                {/* Tray with the sales still waiting */}
                <rect x='80' y='118' width='120' height='56' rx='4' fill={p.machineDark} />
                {trayIndexes.slice().reverse().map(i => (
                    <g key={i} class='at-tray-sheet'>
                        <rect x={96 + i * 3} y={98 - i * 2} width='88' height='60' rx='2' fill={p.sheet} stroke={p.cardStroke} stroke-width='1' />
                        <rect x={106 + i * 3} y={108 - i * 2} width='40' height='4' rx='2' fill={p.line} />
                        <rect x={106 + i * 3} y={118 - i * 2} width='60' height='3' rx='1.5' fill={p.line} />
                    </g>
                ))}
                <rect x='80' y='140' width='120' height='34' rx='4' fill={p.machine} />
                <rect x='92' y='150' width='96' height='4' rx='2' fill={p.machineDark} />
                {trayOverflow > 0 && (
                    <text class='at-tray-overflow' x='205' y='96' font-family='sans-serif' font-size='13' font-weight='700' fill={p.text}>+{trayOverflow}</text>
                )}

                {/* Sealing station */}
                <rect x='340' y='130' width='120' height='44' rx='6' fill={p.machine} />
                <rect x='352' y='126' width='96' height='6' rx='3' fill={p.machineDark} />
                <rect x='352' y='146' width='60' height='4' rx='2' fill={p.machineDark} />
                <circle class='at-anim' cx='440' cy='152' r='5' fill={p.signal} style='animation: atBlink 1.2s ease-in-out infinite;' />

                {inFlight && (
                    <g>
                        {/* The sheet: slides in from the tray, then folds down into the slot */}
                        <g
                            class='at-sheet at-anim'
                            style='transform-origin: center bottom; animation: atSheetIn 0.5s ease-out both, atFold 0.35s ease-in 0.6s forwards;'
                        >
                            <rect x='367' y='62' width='66' height='64' rx='2' fill={p.sheet} stroke={p.cardStroke} stroke-width='1' />
                            <rect x='376' y='72' width='34' height='5' rx='2.5' fill={p.ink} opacity='0.7' />
                            <rect x='376' y='84' width='48' height='3' rx='1.5' fill={p.line} />
                            <rect x='376' y='92' width='42' height='3' rx='1.5' fill={p.line} />
                            <rect x='376' y='100' width='48' height='3' rx='1.5' fill={p.line} />
                            <rect x='376' y='114' width='22' height='4' rx='2' fill={p.ink} opacity='0.6' />
                        </g>

                        {/* The envelope: appears at the station, flies the arc, hovers by the cloud */}
                        <g
                            class='at-envelope at-anim'
                            style='opacity: 0; animation: atEnvelopeIn 0.25s ease-out 0.95s forwards, atFly 1.3s ease-in-out 1.25s forwards, atHover 1.8s ease-in-out 2.55s infinite;'
                        >
                            <rect x='376' y='92' width='48' height='32' rx='3' fill={p.envelope} stroke={p.envelopeEdge} stroke-width='1.5' />
                            <path d='M376 92 L400 112 L424 92' fill='none' stroke={p.envelopeEdge} stroke-width='1.5' />
                            <rect x='412' y='96' width='8' height='8' fill={p.accent} opacity='0.8' />
                        </g>
                    </g>
                )}

                {/* Accounting cloud with antenna */}
                <line x1='665' y1='36' x2='665' y2='24' stroke={p.frame} stroke-width='2' />
                <circle class='at-anim' cx='665' cy='22' r='4' fill={p.signal} style='animation: atBlink 1.2s ease-in-out 0.6s infinite;' />
                {safeCurrent > 0 && (
                    <ellipse
                        class='at-pulse at-anim'
                        cx='652'
                        cy='70'
                        rx='70'
                        ry='36'
                        fill='none'
                        stroke={p.signal}
                        stroke-width='3'
                        style='opacity: 0; animation: atPulse 0.7s ease-out 0.05s forwards;'
                    />
                )}
                <CloudShape p={p} />
                <text class='at-cloud-name' x='652' y='82' text-anchor='middle' font-family='sans-serif' font-size='16' font-weight='800' fill={p.text}>{systemName}</text>

                {/* Counters */}
                <Badge x={606} y={132} fill={p.badge} count={sent} label='Sendt' countClass='at-sent-count' p={p} />
                {safeFailed > 0 && (
                    <Badge x={700} y={132} fill={p.accent} count={safeFailed} label='Feilet' countClass='at-fail-count' p={p} />
                )}

                {/* Labels */}
                <text x='140' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Venter</text>
                <text x='400' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Pakkes</text>
                <text x='652' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Regnskap</text>
            </svg>

            <div class='px-3 pb-3 bg-body text-body'>
                <div class='text-center fw-bold pt-2' style='min-height: 1.5em;'>
                    {label}
                </div>
                <div class='progress mt-2' style='height: 10px;'>
                    <div
                        class='progress-bar bg-success progress-bar-striped progress-bar-animated'
                        role='progressbar'
                        style={`width: ${pct}%;`}
                        aria-valuenow={safeCurrent}
                        aria-valuemin='0'
                        aria-valuemax={total}
                    />
                </div>
                <div class='d-flex justify-content-between small mt-1 text-body-secondary'>
                    <span><i class='fa-solid fa-spinner fa-spin' /> Sender {safeCurrent} av {total}</span>
                    <span>{sent} sendt{safeFailed > 0 ? `, ${safeFailed} feilet` : ''}</span>
                </div>
                <div class='alert alert-warning py-1 mt-2 mb-0 small text-center'>
                    <i class='fa-solid fa-triangle-exclamation' /> Ikke lukk vinduet før overføringen er ferdig.
                </div>
            </div>
        </div>
    );
};

export default AccountingTransferProgress;
